import React, { useEffect, useState, useRef } from 'react';
import { useApp, api } from '@/context/AppContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Trash2, Printer, ArrowLeft } from 'lucide-react';

const DEFAULT_LEVELS = [
  { label: '4', score: 4, descriptor_ar: 'متميز', descriptor_en: 'Excellent' },
  { label: '3', score: 3, descriptor_ar: 'كفء', descriptor_en: 'Proficient' },
  { label: '2', score: 2, descriptor_ar: 'مقبول', descriptor_en: 'Developing' },
  { label: '1', score: 1, descriptor_ar: 'يحتاج دعم', descriptor_en: 'Needs Support' },
];

export default function RubricsPage() {
  const { t, lang } = useApp();
  const [rubrics, setRubrics] = useState([]);
  const [active, setActive] = useState(null);
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');

  useEffect(() => { api.get('/rubrics').then(r => setRubrics(r.data)); }, []);

  const create = async () => {
    if (!name) return;
    const { data } = await api.post('/rubrics', { name_ar: name, criteria: [{ name_ar: lang === 'ar' ? 'الأداء الحركي' : 'Motor Performance', name_en: 'Motor Performance', weight: 1, levels: DEFAULT_LEVELS }] });
    setRubrics([data, ...rubrics]);
    setActive(data);
    setOpen(false);
    setName('');
  };

  const del = async (id) => {
    await api.delete(`/rubrics/${id}`);
    setRubrics(rubrics.filter(r => r.id !== id));
    if (active?.id === id) setActive(null);
  };

  if (active) return <RubricEditor rubric={active} onBack={() => { setActive(null); api.get('/rubrics').then(r => setRubrics(r.data)); }} />;

  return (
    <main id="main" className="max-w-7xl mx-auto px-4 py-8" data-testid="rubrics-page">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-3xl font-black">{t('rubrics_title')}</h1>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button className="bg-primary" data-testid="new-rubric-btn"><Plus className="w-4 h-4 me-1" />{t('rubric_new')}</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>{t('rubric_new')}</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>{t('rubric_name')}</Label><Input value={name} onChange={(e) => setName(e.target.value)} data-testid="rubric-name-input" /></div>
              <Button onClick={create} className="w-full bg-primary" data-testid="rubric-create-btn">{t('save')}</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {rubrics.length === 0 ? (
        <Card><CardContent className="p-12 text-center text-muted-foreground">{t('no_results')}</CardContent></Card>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {rubrics.map(r => (
            <Card key={r.id} className="card-elevate" data-testid={`rubric-${r.id}`}>
              <CardContent className="p-5">
                <div className="flex items-start justify-between mb-2">
                  <h3 className="font-bold">{r.name_ar}</h3>
                  <button onClick={() => del(r.id)} aria-label="delete" className="text-muted-foreground hover:text-destructive" data-testid={`del-rubric-${r.id}`}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
                <p className="text-xs text-muted-foreground mb-3">{(r.criteria || []).length} {lang === 'ar' ? 'معيار' : 'criteria'}</p>
                <Button size="sm" variant="outline" className="w-full" onClick={() => setActive(r)} data-testid={`open-rubric-${r.id}`}>{t('plan_open')}</Button>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </main>
  );
}

function RubricEditor({ rubric, onBack }) {
  const { t, lang } = useApp();
  const [criteria, setCriteria] = useState(rubric.criteria || []);
  const printRef = useRef(null);

  const setC = (idx, patch) => setCriteria(criteria.map((c, i) => i === idx ? { ...c, ...patch } : c));
  const addC = () => setCriteria([...criteria, { name_ar: '', name_en: '', weight: 1, levels: DEFAULT_LEVELS }]);
  const removeC = (idx) => setCriteria(criteria.filter((_, i) => i !== idx));

  const save = async () => {
    await api.delete(`/rubrics/${rubric.id}`);
    const { data } = await api.post('/rubrics', { name_ar: rubric.name_ar, criteria });
    toast.success(t('saved'));
    rubric.id = data.id;
  };

  const printPdf = () => {
    const w = window.open('', '_blank');
    if (!w) return;
    const isAr = lang === 'ar';
    const colHeader = isAr ? 'المعيار' : 'Criterion';
    w.document.write(`
      <!doctype html><html lang="${lang}" dir="${isAr ? 'rtl' : 'ltr'}">
      <head><meta charset="utf-8"><title>${rubric.name_ar}</title>
      <style>
        body { font-family: 'Cairo','IBM Plex Sans Arabic', Arial, sans-serif; padding: 30px; color: #1C1917; }
        h1 { color: #14532D; border-bottom: 2px solid #14532D; padding-bottom: 8px; }
        table { width: 100%; border-collapse: collapse; margin-top: 20px; }
        th, td { border: 1px solid #888; padding: 10px; text-align: ${isAr ? 'right' : 'left'}; vertical-align: top; }
        th { background: #14532D; color: white; }
        td.criterion { background: #f5f5f0; font-weight: 700; width: 25%; }
        .footer { margin-top: 30px; font-size: 12px; color: #666; }
      </style></head><body>
      <h1>${rubric.name_ar}</h1>
      <table>
        <thead><tr>
          <th>${colHeader}</th>
          ${criteria[0]?.levels?.map(l => `<th>${l.label} — ${isAr ? l.descriptor_ar : l.descriptor_en}</th>`).join('') || ''}
        </tr></thead>
        <tbody>
          ${criteria.map(c => `
            <tr>
              <td class="criterion">${isAr ? c.name_ar : (c.name_en || c.name_ar)}${c.weight && c.weight !== 1 ? ` (×${c.weight})` : ''}</td>
              ${c.levels.map(l => `<td>${isAr ? l.descriptor_ar : l.descriptor_en}</td>`).join('')}
            </tr>
          `).join('')}
        </tbody>
      </table>
      <div class="footer">${isAr ? 'منصة همم — التربية الرياضية المعدّلة' : 'Himam Platform — Adapted PE'}</div>
      <script>window.onload = () => { window.print(); }</script>
      </body></html>
    `);
    w.document.close();
  };

  return (
    <main id="main" className="max-w-6xl mx-auto px-4 py-6" data-testid="rubric-editor">
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={onBack} data-testid="rubric-back"><ArrowLeft className="w-4 h-4 me-1" />{t('prev')}</Button>
          <h1 className="text-2xl font-black">{rubric.name_ar}</h1>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={printPdf} data-testid="rubric-print"><Printer className="w-4 h-4 me-1" />{t('rubric_export')}</Button>
          <Button size="sm" className="bg-primary" onClick={save} data-testid="rubric-save">{t('save')}</Button>
        </div>
      </div>

      <div className="space-y-4" ref={printRef}>
        {criteria.map((c, idx) => (
          <Card key={idx} data-testid={`criterion-${idx}`}>
            <CardHeader className="flex flex-row items-center justify-between">
              <div className="flex-1 grid sm:grid-cols-3 gap-2">
                <Input placeholder={lang === 'ar' ? 'اسم المعيار (عربي)' : 'Criterion name (Arabic)'} value={c.name_ar} onChange={(e) => setC(idx, { name_ar: e.target.value })} data-testid={`crit-name-ar-${idx}`} />
                <Input placeholder={lang === 'ar' ? 'الاسم (إنجليزي)' : 'Name (English)'} value={c.name_en || ''} onChange={(e) => setC(idx, { name_en: e.target.value })} data-testid={`crit-name-en-${idx}`} />
                <Input type="number" min="0.1" step="0.1" placeholder={t('rubric_weight')} value={c.weight} onChange={(e) => setC(idx, { weight: Number(e.target.value) })} data-testid={`crit-weight-${idx}`} />
              </div>
              <button onClick={() => removeC(idx)} className="ms-2 text-muted-foreground hover:text-destructive" data-testid={`del-crit-${idx}`}><Trash2 className="w-4 h-4" /></button>
            </CardHeader>
            <CardContent>
              <div className="grid sm:grid-cols-4 gap-2">
                {c.levels.map((l, li) => (
                  <div key={li} className="border border-border rounded p-2">
                    <div className="text-xs font-bold mb-1">{l.label}</div>
                    <Textarea
                      rows={3}
                      value={lang === 'ar' ? l.descriptor_ar : l.descriptor_en}
                      onChange={(e) => {
                        const newLevels = [...c.levels];
                        newLevels[li] = { ...newLevels[li], [lang === 'ar' ? 'descriptor_ar' : 'descriptor_en']: e.target.value };
                        setC(idx, { levels: newLevels });
                      }}
                      data-testid={`crit-${idx}-level-${li}`}
                      className="text-xs"
                    />
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        ))}
        <Button variant="outline" onClick={addC} data-testid="add-criterion-btn"><Plus className="w-4 h-4 me-1" />{t('rubric_add_criterion')}</Button>
      </div>
    </main>
  );
}
